/*
 * WaWiAuftragEinerWert.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-11-24 in huWaWi3 33.11.2
 * Copyright (c) 2023 HUDORA GmbH
 */

import { LadeChecker } from '@hudora/hd-react-components'
import { ValueRender } from '@hudora/react-jsonschema-valuerender'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Collapse from '@mui/material/Collapse'
import IconButton, { IconButtonProps } from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import * as React from 'react'
import { useQuery } from 'urql'

import { HwAuftrag } from '../../gql/graphql'
import { AUFTRAGSWERT_QUERY } from './WaWiAuftragEinerQuery'

export function AuftragsWert({ auftrag }: { auftrag: HwAuftrag }) {
  const [expanded, setExpanded] = React.useState(false)
  const [{ fetching, error, data }] = useQuery({
    query: AUFTRAGSWERT_QUERY,
    variables: { nr: auftrag.auftragsnr },
  })

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  return (
    <LadeChecker loading={fetching} error={error}>
      {data ? (
        <>
          <ValueRender value={data.entity?.wertBerechnung?.bestellt?.netto_betrag} format="eurocentlarge" />{' '}
          {' / '}
          <ValueRender value={data.entity?.wertBerechnung?.geliefert?.netto_betrag} format="eurocentlarge" />
          {' / '}
          <ValueRender value={data.entity?.wertBerechnung?.offen?.netto_betrag} format="eurocentlarge" />{' '}
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
          <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ mb: '1em' }}>
            <TableContainer component={Paper}>
              <Table size={'small'} sx={{ minWidth: 650 }}>
                <caption>Die Werte sind geschätzt.</caption>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>bestellt</TableCell>
                    <TableCell>geliefert</TableCell>
                    <TableCell>offen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: 'fh', name: 'Listenpreis', title: 'Preise, wie sie in der Preisliste stehen.' },
                    {
                      key: 'kundenPreis',
                      name: 'Kundenspezifischer Preis',
                      title: 'Preise auch aus kunden-/gruppen-spezifischen Preislisten.',
                    },
                    {
                      key: 'warenwert',
                      name: 'Auftragspreis',
                      title: 'Preise, wie sie in der Auftragsposition gepflegt wurden.',
                    },
                    // { key: 'summe_netto_betraege', name: 'mit Positionsrabatten' }, // Summe einzelner Positionen incl. Positions-Abschläge. Noch ohne Kopf-Abschläge.
                    { key: 'netto_betrag', name: 'Netto-Preis' }, // Zu zahlen ohne Steuer. Warenwert - positionsbezogene Rabatte. / Zu zahlen ohne Steuer. Warenwert Netto - Rabatte %. Z.B. MOA+125:209.53:EUR' in EDI. Warenwert - Rabatte. Hier drauf wird die Steuer erhoben

                    // { key: 'steuer_betrag', name: 'Steuer' },  // Höhe der Steuer
                    { key: 'abschlag_betrag', name: 'Rabatte' }, // Auf den Warenwert fällige Rabatte in Eurocent
                    { key: 'brutto_betrag', name: 'Brutto' }, // Rabattierter und besteuerter Wert
                  ].map((x) => (
                    <TableRow>
                      <TableCell title={x.title}>{x.name}</TableCell>
                      <TableCell>
                        <ValueRender
                          value={data.entity.wertBerechnung?.bestellt?.[x.key]}
                          format="eurocent"
                        />
                      </TableCell>
                      <TableCell>
                        <ValueRender
                          value={data.entity.wertBerechnung?.geliefert?.[x.key]}
                          format="eurocent"
                        />
                      </TableCell>
                      <TableCell>
                        <ValueRender value={data.entity.wertBerechnung?.offen?.[x.key]} format="eurocent" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {auftrag.auftragsrabatt_prozent ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'right' }}>
                        Auftragspreis abzüglich {auftrag.auftragsrabatt_prozent}% auftragsspezifischer Rabatt
                        "{auftrag.auftragsrabatt_text}"
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {data.entity.kunde.kundenrabatt_prozent ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'right' }}>
                        Auftragspreis abzüglich {data.entity.kunde.kundenrabatt_prozent}% kundenspezifischer
                        Rabatt "{data.entity.kunde.kundenrabatt_text}"
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </>
      ) : null}
    </LadeChecker>
  )
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props
  return <IconButton {...other} />
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}))
